import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { BankOnboardingsTable } from 'src/components/payments-ops/bank-onboarding/BankOnboardingsTable'
import { BankOnboardingsToolbar } from 'src/components/payments-ops/bank-onboarding/BankOnboardingsToolbar'
import { PageHeader } from 'src/components/payments-ops/PageHeader'
import { Button } from 'src/components/shared/Button'
import { AdminFetchBankOnboardings } from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { BankOnboardingStatus } from 'src/constants/payments-ops/enums'
import { buildNewBankOnboardingPath } from 'src/constants/routes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { BankOnboarding, FetchBankOnboardingsPayload } from 'src/interfaces/bankOnboarding'
import { getBankOnboardingsError } from 'src/selectors/bankOnboarding'
import { useQueryParamUpdater } from 'src/utils/hooks/paymentsOps/useQueryParamUpdater'

const STATUSES = Object.values(BankOnboardingStatus) as string[]

const BankOnboardingPageComponent = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const updateParams = useQueryParamUpdater()
  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const listError = useSelector(getBankOnboardingsError)
  // "New onboarding" and "Start new from this" both lead to `POST /payments/bank-onboardings`
  // (`bank_onboarding_manage`); the `/new` route is guarded by the same permission.
  const canManageOnboardings = useHasPermission(InternalPermission.BankOnboardingManage)

  const [refreshToken, setRefreshToken] = useState<number>(0)

  const search = searchParams.get('search') ?? ''
  const statusParam = searchParams.get('status') ?? ''
  const status = STATUSES.includes(statusParam) ? (statusParam as BankOnboardingStatus) : null

  const request = useMemo<FetchBankOnboardingsPayload>(() => {
    return { search: search === '' ? null : search, status }
  }, [search, status])

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchBankOnboardings.request(request))
  }, [asyncDispatchWithErrorToast, isAuthenticated, refreshToken, request])

  const handleSearchChange = useCallback(
    (nextSearch: string) => {
      const trimmedSearch = nextSearch.trim()

      // TableSearch fires its debounced callback on mount with the unchanged value; rewriting
      // the URL for a no-op would add churn (and a needless refetch path).
      if (trimmedSearch === search) {
        return
      }

      updateParams({ search: trimmedSearch === '' ? null : trimmedSearch })
    },
    [search, updateParams],
  )

  const handleStatusChange = useCallback(
    (nextStatus: BankOnboardingStatus | null) => {
      if (nextStatus === status) {
        return
      }

      updateParams({ status: nextStatus })
    },
    [status, updateParams],
  )

  const handleRefresh = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  const handleNew = useCallback(() => {
    navigate(buildNewBankOnboardingPath())
  }, [navigate])

  const handleStartNewFrom = useCallback(
    (onboarding: BankOnboarding) => {
      navigate(buildNewBankOnboardingPath(onboarding.id))
    },
    [navigate],
  )

  return (
    <div
      data-testid="bank-onboarding-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-6`}
    >
      <PageHeader
        title="Bank Onboarding"
        description="Latest US Bank customer onboarding per partner."
        actions={
          canManageOnboardings ? (
            <Button size="small" color="dark" onClick={handleNew} data-testid="bank-onboarding-new">
              New onboarding
            </Button>
          ) : undefined
        }
      />
      <BankOnboardingsToolbar
        search={search}
        status={status}
        onSearchChange={handleSearchChange}
        onStatusChange={handleStatusChange}
        onRefresh={handleRefresh}
      />
      {listError ? (
        <Typography variant="body2" css={tw`bg-shades-0 px-4 py-3 text-critical-500`}>
          {listError}
        </Typography>
      ) : null}
      <BankOnboardingsTable onStartNewFrom={canManageOnboardings ? handleStartNewFrom : null} />
    </div>
  )
}

export const BankOnboardingPage = memo(BankOnboardingPageComponent)
